import DataToolbar from '@/components/DataToolbar';
import DatePeriodPicker from '@/components/DatePeriodPicker';
import { variantSummary } from "@/lib/variant-quantities.mjs";
import { returnCreditStorageKey } from '@/lib/scope';
import WriteButton from "@/components/WriteButton";
import { useEffect, useState, useRef } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ReportActions from '@/components/ReportActions';
import Pagination from '@/components/Pagination';
import { fetchAllHistory } from '@/lib/history-pages';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ReceiptModal from "@/components/ReceiptModal";
import { transactionPeriod, TRANSACTION_TIME_ZONE } from "@/lib/transaction-period.mjs";
import { Receipt, ChevronRight, Search, Undo2, Minus, Plus, Printer, X, RefreshCw, CheckCircle2, AlertTriangle, MoreVertical } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSupervisorApproval } from "@/components/SupervisorApprovalDialog";
import { printReturnReceipt } from "@/lib/printReturnReceipt";

const badge = {
  tunai: "bg-brand-soft text-brand-readable",
  transfer: "bg-accent-soft text-accent-readable",
  qris: "bg-accent-soft text-accent-readable",
  split: "bg-warning-soft text-warning-readable",
};

const rkey = (x) => `${x.product_id}|${x.name}|${x.price}`;
const PERIODS = [["today", "Hari Ini"], ["this-month", "Bulan Ini"], ["last-month", "Bulan Lalu"]];

const transactionSyncState = (tx) => {
  const refundStates = (tx.refunds || []).map((refund) => refund.akunkas_sync_status);
  if (tx.akunkas_sync_status === "failed" || refundStates.includes("failed")) return "failed";
  if (tx.akunkas_sync_status === "pending" || refundStates.includes("pending")) return "pending";
  if (tx.akunkas_sync_status === "synced" || refundStates.includes("synced")) return "synced";
  return tx.akunkas_sync_status || "not_configured";
};

// remaining qty per line after existing refunds
const remainingPerLine = (tx) => {
  const pool = {};
  (tx.refunds || []).forEach((r) => r.items.forEach((it) => { const k = rkey(it); pool[k] = (pool[k] || 0) + it.qty; }));
  return tx.items.map((it) => {
    const k = rkey(it);
    const used = Math.min(it.qty, pool[k] || 0);
    pool[k] = (pool[k] || 0) - used;
    return it.qty - used;
  });
};

export default function Transactions() {
  const { approveAction, approvalDialog } = useSupervisorApproval();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [txs, setTxs] = useState([]);
  const [returnRows, setReturnRows] = useState([]);
  const [returnTotal, setReturnTotal] = useState(0);
  const [viewMode, setViewMode] = useState('sales');
  const [filters, setFilters] = useState(() => ({ ...transactionPeriod("today"), search: "" }));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRequest = useRef(null);
  const [selected, setSelected] = useState(null);
  const [refundQty, setRefundQty] = useState({});
  const [refundMethod, setRefundMethod] = useState("tunai");
  const [returnType, setReturnType] = useState("return");
  const [resolution, setResolution] = useState("refund");
  const [returnMeta, setReturnMeta] = useState({});
  const [returnNote, setReturnNote] = useState("");
  const [differenceMethod, setDifferenceMethod] = useState("tunai");
  const [products, setProducts] = useState([]);
  const [noReceiptSearch, setNoReceiptSearch] = useState("");
  const [reverseSettlement, setReverseSettlement] = useState(false);
  const [refundMethods, setRefundMethods] = useState([]);
  const [refunding, setRefunding] = useState(false);
  const [reprint, setReprint] = useState(null);
  const [syncStatus, setSyncStatus] = useState({ pending: 0, failed: 0, total: 0, items: [] });
  const [syncing, setSyncing] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);

  const loadSyncStatus = async () => {
    try {
      const { data } = await api.get("/akunkas/sync-status");
      setSyncStatus(data);
    } catch {
      // Riwayat transaksi tetap dapat digunakan bila status integrasi gagal dimuat.
    }
  };

  const dateError = !filters.start || !filters.end ? "Lengkapi tanggal awal dan akhir."
    : filters.start > filters.end ? "Tanggal awal tidak boleh melewati tanggal akhir." : "";
  const selectedPeriod = PERIODS.find(([key]) => {
    const period = transactionPeriod(key);
    return filters.start === period.start && filters.end === period.end;
  })?.[1] || `${filters.start} – ${filters.end}`;
  const updateFilters = (values) => {
    setLoading(true);
    setPage(1);
    setFilters((previous) => ({ ...previous, ...values }));
  };

  useEffect(() => {
    const controller = new AbortController();
    setTxs([]);
    setReturnRows([]);
    setLoadError("");
    if (dateError) { setLoading(false); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = { start: filters.start, end: filters.end, q: filters.search.trim() || undefined };
        if (viewMode === 'returns') {
          const { data } = await api.get('/returns/history', { signal: controller.signal, params: {
            ...params, limit: pageSize === 'all' ? 100 : pageSize, offset: (page - 1) * (pageSize === 'all' ? 100 : pageSize),
          } });
          if (controller.signal.aborted) return;
          setReturnRows(data.items || []); setReturnTotal(data.total || 0);
          return;
        }
        if (pageSize === 'all') {
          await fetchAllHistory(params, { signal: controller.signal, onProgress: (items, count) => {
            setTxs(items); if (count != null) setTotal(count);
          } });
        } else {
          const { data } = await api.get('/transactions/history', { signal: controller.signal, params: { ...params, limit: pageSize, offset: (page - 1) * pageSize } });
          if (controller.signal.aborted) return;
          setTotal(data.total);
          if (page > 1 && !data.items.length && data.total > 0) setPage(Math.ceil(data.total / pageSize));
          else setTxs(data.items);
        }
      } catch (e) {
        if (!controller.signal.aborted) setLoadError(apiError(e));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [filters, dateError, page, pageSize, viewMode]);

  useEffect(() => () => detailRequest.current?.abort(), []);

  useEffect(() => {
    api.get("/payment-methods")
      .then((response) => setRefundMethods(response.data.filter((method) => method.active)))
      .catch(() => setRefundMethods([
        { id: "tunai", label: "Tunai" },
        { id: "transfer", label: "Transfer" },
        { id: "qris", label: "QRIS" },
      ]));
  }, []);

  useEffect(() => { loadSyncStatus(); }, []);

  useEffect(() => {
    const transactionId = searchParams.get("transaction_id");
    if (!transactionId) return;
    api.get(`/transactions/${transactionId}`)
      .then(({ data }) => openTx(data))
      .catch((error) => toast.error(apiError(error)));
    // Hanya buka ulang bila ID transaksi pada URL berubah.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/akunkas/sync-failed");
      setSyncStatus(data);
      if (data.total === 0) {
        toast.success("Semua data berhasil disinkronkan", { description: `${data.attempted} data diperiksa` });
      } else {
        toast.error(`${data.total} data masih belum tersinkron`, { description: "Periksa daftar status untuk penyebabnya" });
      }
      updateFilters({});
    } catch (error) {
      toast.error(apiError(error));
      await loadSyncStatus();
    } finally {
      setSyncing(false);
    }
  };

  const openTx = async (t) => {
    detailRequest.current?.abort();
    setDetailLoading(false);
    if (t.summary_only) {
      const controller = new AbortController(); detailRequest.current = controller;
      setDetailLoading(true);
      try {
        const { data } = await api.get(`/transactions/${t.id}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        t = data;
      } catch (error) {
        if (!controller.signal.aborted) toast.error(apiError(error));
        return;
      } finally { if (!controller.signal.aborted) setDetailLoading(false); }
    }
    setSelected(t); setRefundQty({}); setRefundMethod("tunai"); setDifferenceMethod("tunai");
    setReturnType("return"); setResolution((t.credit || 0) > (t.credit_returned || 0) ? "debt_reduction" : "refund");
    setReturnMeta({}); setReturnNote("");
    setReverseSettlement(false);
  };

  const openNoReceiptReturn = () => {
    detailRequest.current?.abort(); setDetailLoading(false);
    setSelected({ no_receipt: true, invoice_no: "TANPA NOTA", items: [], refunds: [], total: 0, created_at: new Date().toISOString(), cashier: "-" });
    setRefundQty({}); setRefundMethod("tunai"); setDifferenceMethod("tunai"); setReturnType("return"); setResolution("refund");
    setReturnMeta({}); setReturnNote(""); setNoReceiptSearch(""); setReverseSettlement(false);
  };

  const remaining = selected ? (selected.no_receipt ? selected.items.map(() => 999999) : remainingPerLine(selected)) : [];
  const refundAmount = selected
    ? selected.items.reduce((s, it, i) => s + (refundQty[i] || 0) * it.price, 0)
    : 0;
  const allRemainingSelected = selected && remaining.every((qty, index) => qty === 0 || (refundQty[index] || 0) === qty);
  const canReverseSettlement = !!(selected?.settlement_total > 0 && !selected?.settlement_reversed_at);
  const totalRefundAmount = refundAmount + (reverseSettlement ? selected?.settlement_total || 0 : 0);

  const setQ = (i, v) => setRefundQty((m) => ({ ...m, [i]: Math.max(0, Math.min(remaining[i], v)) }));
  const setAll = () => { const m = {}; selected.items.forEach((_, i) => { m[i] = remaining[i]; }); setRefundQty(m); };
  const setHalf = () => { const m = {}; selected.items.forEach((_, i) => { m[i] = Math.floor(remaining[i] / 2); }); setRefundQty(m); };

  useEffect(() => {
    if (!selected?.no_receipt || products.length) return;
    api.get("/products").then(({ data }) => setProducts(data.filter((product) => product.active !== false))).catch((error) => toast.error(apiError(error)));
  }, [products.length, selected?.no_receipt]);

  const updateReturnMeta = (index, values) => setReturnMeta((current) => ({
    ...current, [index]: { condition: "saleable", return_to_stock: true, ...(current[index] || {}), ...values },
  }));
  const productPrice = (product) => Number(product.price_tiers?.[0]?.price || product.units?.[0]?.price || 0);
  const addNoReceiptProduct = (product) => {
    const existing = selected.items.findIndex((item) => item.product_id === product.id);
    if (existing >= 0) setRefundQty((quantities) => ({ ...quantities, [existing]: (quantities[existing] || 0) + 1 }));
    else {
      const index = selected.items.length;
      setSelected({ ...selected, items: [...selected.items, { product_id: product.id, name: product.name, qty: 1, price: productPrice(product), pieces: 1 }] });
      setRefundQty((quantities) => ({ ...quantities, [index]: 1 }));
    }
    setNoReceiptSearch("");
  };
  const doRefund = async () => {
    const items = selected.items
      .map((it, i) => ({ product_id: it.product_id, name: it.name, price: it.price, pieces: it.pieces || 1, qty: refundQty[i] || 0,
        condition: returnMeta[i]?.condition || "saleable", return_to_stock: returnMeta[i]?.return_to_stock !== false }))
      .filter((x) => x.qty > 0);
    if (items.length === 0) return toast.error("Pilih jumlah barang yang direfund");
    let approval;
    if (returnType === "void" && !allRemainingSelected) return toast.error("Void/Cancel harus memilih seluruh sisa barang");
    if (returnType === "void" && resolution === "exchange") return toast.error("Tukar produk hanya tersedia melalui Retur Barang");
    if (returnType === "void" && !window.confirm(`Batalkan seluruh transaksi ${selected.invoice_no}?`)) return;
    try { approval = await approveAction("refund", `${selected.invoice_no} · ${returnType === "void" ? "Void/Cancel" : "Retur"} ${rupiah(refundAmount)}`); }
    catch (error) { return toast.error(apiError(error)); }
    if (approval === false) return;
    setRefunding(true);
    try {
      const endpoint = selected.no_receipt ? "/returns/no-receipt" : `/transactions/${selected.id}/refund`;
      const { data } = await api.post(endpoint, {
        items, refund_method: refundMethod, reverse_settlement: reverseSettlement,
        return_type: returnType, resolution, exchange_items: [],
        difference_method: differenceMethod, note: returnNote || null,
      }, approval);
      const methodLabel = refundMethods.find((method) => method.id === refundMethod)?.label || refundMethod;
      const completedReturn = selected.no_receipt ? data : data.refunds?.[data.refunds.length - 1];
      if (resolution === "exchange" && completedReturn?.return_credit_id) {
        localStorage.setItem(returnCreditStorageKey(), completedReturn.return_credit_id);
        toast.success(`Saldo retur ${rupiah(completedReturn.item_amount)} siap digunakan di Kasir`);
        navigate(`/pos?return_credit=${completedReturn.return_credit_id}`);
        return;
      }
      toast.success(`${returnType === "void" ? "Void/Cancel" : "Retur"} ${rupiah(refundAmount)} berhasil`, {
        description: completedReturn?.debt_reduction_amount ? `Utang berkurang ${rupiah(completedReturn.debt_reduction_amount)}` : `Pengembalian default ${methodLabel}`,
      });
      if (selected.no_receipt) setSelected((current) => ({ ...current, items: [], refunds: [completedReturn], completed: true }));
      else setSelected(data);
      setRefundQty({});
      setReturnMeta({}); setReturnNote(""); setReturnType("return");
      setReverseSettlement(false);
      if (!selected.no_receipt) setTxs((prev) => prev.map((t) => (t.id === data.id ? data : t)));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRefunding(false);
    }
  };

  const doReprint = (tx) => {
    if (!tx.refund_total) { setReprint(tx); return; }
    const rem = remainingPerLine(tx);
    const items = tx.items.map((it, i) => ({ ...it, qty: rem[i] })).filter((it) => it.qty > 0);
    const subtotal = items.reduce((s, it) => s + it.qty * it.price, 0);
    const method = tx.payment_type === "split" ? "tunai" : tx.payment_type;
    setReprint({
      id: tx.id,
      invoice_no: `${tx.invoice_no} (REVISI)`,
      items,
      subtotal,
      discount: 0,
      total: subtotal,
      previous_debts: tx.previous_debts || [],
      settlement_total: tx.settlement_total || 0,
      payments: [{ method, amount: subtotal + (tx.settlement_total || 0) }],
      paid: subtotal + (tx.settlement_total || 0),
      change: 0,
      created_at: tx.created_at,
      cashier: tx.cashier,
      note: tx.note,
    });
  };

  const cancelPendingExchange = async (record) => {
    if (!record.return_credit_id || !window.confirm(`Batalkan tukar ${record.return_no} dan kembalikan ${rupiah(record.item_amount)} secara Tunai?`)) return;
    try {
      const approval = await approveAction("refund", `Batalkan tukar ${record.return_no} · refund Tunai ${rupiah(record.item_amount)}`);
      if (approval === false) return;
      const { data: result } = await api.post(`/return-credits/${record.return_credit_id}/cancel`, {}, approval);
      const { data } = await api.get(`/transactions/${selected.id}`);
      setSelected(data);
      setTxs((previous) => previous.map((transaction) => transaction.id === data.id ? data : transaction));
      toast.success(`Tukar dibatalkan · kembalikan Tunai ${rupiah(result.cash_refund_amount)}`, {
        description: result.queue_removed ? "Antrian tukar juga sudah dihapus." : undefined,
      });
    } catch (error) { toast.error(apiError(error)); }
  };

  const anyRefundSelected = Object.values(refundQty).some((v) => v > 0);
  const canRefundMore = remaining.some((r) => r > 0);
  const noReceiptMatches = noReceiptSearch.trim().length < 2 ? [] : products.filter((product) => {
    const query = noReceiptSearch.trim().toLowerCase();
    return product.name.toLowerCase().includes(query) || String(product.barcode || "").toLowerCase().includes(query);
  }).slice(0, 8);

  return (
    <>
      {approvalDialog}
      <PageHeader title={viewMode === 'returns' ? 'Riwayat Retur & Refund' : 'Transaksi'} subtitle={viewMode === 'returns' ? 'Retur dari nota dan retur tanpa nota · Waktu Indonesia Barat (WIB)' : 'Riwayat transaksi · Waktu Indonesia Barat (WIB)'} />

      <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
        <div className="tx-tablet-toolbar hidden min-[641px]:flex xl:hidden items-stretch gap-2" data-testid="tx-tablet-toolbar">
          <div className={`flex w-full min-w-0 items-center gap-1.5 rounded-xl border px-2 py-2 ${syncStatus.total > 0 ? "border-warning bg-warning-soft" : "border-brand/30 bg-brand-soft"}`}>
            {syncStatus.total > 0 ? <AlertTriangle className="h-4 w-4 shrink-0 text-warning-readable" /> : <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-readable" />}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[11px] font-semibold">Buku Kas</div>
              <div className="truncate text-[10px] text-ui-muted">{syncStatus.total > 0 ? `${syncStatus.failed} gagal · ${syncStatus.pending} tertunda` : "Sudah tersinkron"}</div>
            </div>
            <Button onClick={syncAll} disabled={syncing} data-testid="sync-all-akunkas" aria-label={syncing ? "Sedang menyinkronkan" : "Sinkronkan semua"} title="Sinkronkan semua" className="h-8 w-8 shrink-0 p-0">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        <div className={`block min-[641px]:hidden min-[1280px]:block rounded-xl border p-2.5 sm:p-4 ${syncStatus.total > 0 ? "border-warning bg-warning-soft" : "border-brand/30 bg-brand-soft"}`} data-testid="akunkas-sync-panel">
          <div className="flex items-center sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-2 sm:gap-3">
              {syncStatus.total > 0
                ? <AlertTriangle className="h-5 w-5 mt-0.5 text-warning-readable" />
                : <CheckCircle2 className="h-5 w-5 mt-0.5 text-brand-readable" />}
              <div>
                <div className="font-semibold text-sm sm:text-base">Sinkronisasi Buku Kas</div>
                <div className="text-[11px] sm:text-xs text-ui-muted mt-0.5">
                  {syncStatus.total > 0
                    ? `${syncStatus.failed} gagal · ${syncStatus.pending} tertunda`
                    : "Penjualan, refund, piutang, dan pelunasan sudah tersinkron"}
                </div>
              </div>
            </div>
            <Button onClick={syncAll} disabled={syncing} data-testid="sync-all-akunkas" aria-label={syncing ? "Sedang menyinkronkan" : "Sinkronkan semua"} title="Sinkronkan semua" className="h-9 w-9 shrink-0 p-0 sm:h-10 sm:w-auto sm:px-3 sm:gap-2">
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">{syncing ? "Menyinkronkan…" : "Sinkronkan Semua"}</span>
            </Button>
          </div>
          {syncStatus.items?.length > 0 && (
            <div className="hidden sm:block mt-3 border-t border-ui-line/60 pt-2 space-y-1">
              {syncStatus.items.slice(0, 5).map((item) => (
                <div key={`${item.type}-${item.id}`} className="text-xs flex flex-wrap justify-between gap-2">
                  <span>{item.reference}</span>
                  <span className={item.status === "failed" ? "text-danger-readable" : "text-warning-readable"}>
                    {item.status === "failed" ? "Gagal" : "Tertunda"}{item.error ? ` · ${item.error}` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-wrap items-end justify-between gap-3 rounded-xl border border-ui-line bg-ui-card p-3" data-testid="tx-date-actions">
        <TransactionPeriodMenu className="flex sm:hidden min-h-10 min-w-0 flex-1" testPrefix="tx-compact" selectedPeriod={selectedPeriod} filters={filters} dateError={dateError} periodOpen={periodOpen} setPeriodOpen={setPeriodOpen} updateFilters={updateFilters} />
        <div className="hidden min-w-0 sm:block" data-testid="tx-period-picker">
          <DatePeriodPicker start={filters.start} end={filters.end} onChange={updateFilters} label={viewMode === 'returns' ? 'Periode retur' : 'Periode transaksi'} testId="tx-date" />
        </div>
        <div className="ml-auto min-w-0 max-w-full [&>[data-testid=report-actions]]:my-0 [&>[data-testid=report-actions]]:justify-end [&_[data-testid=report-actions]>span]:w-full [&_[data-testid=report-actions]>span]:text-right" data-testid="tx-report-tools">
          {viewMode === 'sales' && <ReportActions title="Riwayat Transaksi" disabled={loading || !!loadError || !!dateError} totalRows={total} getRows={async () => (await fetchAllHistory({ start: filters.start, end: filters.end, q: filters.search.trim() || undefined })).map(t => [t.invoice_no,t.created_at,t.customer_name,t.cashier,t.payment_type,t.total,t.refund_total ?? 0,transactionSyncState(t)])} headers={['No Transaksi','Tanggal','Pelanggan','Kasir','Pembayaran','Total Asal','Retur','Status Sinkronisasi']} rows={txs.map(t => [t.invoice_no,t.created_at,t.customer_name,t.cashier,t.payment_type,t.total,t.refund_total ?? 0,transactionSyncState(t)])} notes={[`Periode: ${filters.start || '—'} sampai ${filters.end || '—'}`,`Pencarian: ${filters.search || 'Semua'}`, 'Angka total adalah transaksi asal; nilai retur disajikan terpisah.']} />}
        </div>
        </div>
        {loadError && <div role="alert" className="rounded-lg border border-danger bg-danger-soft p-3 text-sm text-danger-readable">
          {loadError} <button type="button" onClick={() => updateFilters({})} className="ml-2 underline">Coba lagi</button>
        </div>}
        <DataToolbar label="Alat tabel transaksi">
<Pagination page={page} pageSize={viewMode === 'returns' && pageSize === 'all' ? 100 : pageSize} total={viewMode === 'returns' ? returnTotal : total} loading={loading} label={viewMode === 'returns' ? 'Halaman riwayat retur' : 'Halaman riwayat transaksi'} allowAll={viewMode === 'sales'} onPageChange={value => { setLoading(true); setPage(value); }} onPageSizeChange={value => { setLoading(true); setPage(1); setPageSize(value); }}>
<div className="relative min-w-0 flex-1 basis-28 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
          <Input
            data-testid="tx-search-input"
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
            maxLength={100}
            aria-label={viewMode === 'returns' ? 'Cari nomor retur, invoice, produk, atau pelanggan' : 'Cari nomor invoice atau nama pelanggan dalam periode'}
            placeholder={viewMode === 'returns' ? 'Cari retur, invoice, produk…' : 'Cari invoice atau pelanggan…'}
            className="pl-10 bg-ui-card border-ui-line h-10"
          />
        </div>
        <Button type="button" variant={viewMode === 'returns' ? 'default' : 'outline'} onClick={() => { setLoading(true); setPage(1); setPageSize(10); setViewMode(value => value === 'returns' ? 'sales' : 'returns'); }} data-testid="tx-return-history-toggle" className="h-10 shrink-0 whitespace-nowrap"><Undo2 className="h-4 w-4 mr-2" />{viewMode === 'returns' ? 'Kembali ke Transaksi' : 'Riwayat Retur'}</Button>
        <Button type="button" variant="outline" onClick={openNoReceiptReturn} data-testid="tx-no-receipt-return" className="h-10 shrink-0 border-ui-line whitespace-nowrap"><Undo2 className="h-4 w-4 mr-2" /> Retur Tanpa Nota</Button>
</Pagination>
</DataToolbar>
        {detailLoading && <p role="status" className="text-sm text-ui-muted">Memuat detail nota…</p>}
        {viewMode === 'sales' ? <div className="rounded-xl border border-ui-line overflow-x-auto" data-testid="transactions-table" aria-busy={loading}>
          <table className="w-full text-sm">
            <thead className="bg-ui-card text-ui-muted text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">No. Invoice</th>
                <th className="text-left p-3">Pelanggan</th>
                <th className="text-left p-3 hidden md:table-cell">Waktu</th>
                <th className="text-left p-3 hidden sm:table-cell">Kasir</th>
                <th className="text-center p-3">Bayar</th>
                <th className="text-center p-3 hidden md:table-cell">Sinkron</th>
                <th className="text-right p-3">Total</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {!dateError && txs.map((t) => (
                <tr key={t.id} data-testid={`tx-row-${t.id}`} onClick={() => openTx(t)} className="border-t border-ui-line hover:bg-ui-soft cursor-pointer">
                  <td className="p-3 font-mono">
                    {t.invoice_no}
                    {t.status === "void" ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-danger-soft text-danger-readable font-sans">VOID</span>
                      : t.refund_total > 0 && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-danger-soft text-danger-readable font-sans">RETUR</span>}
                  </td>
                  <td className="p-3 max-w-48 truncate" title={t.customer_name || "Pelanggan umum"}>{t.customer_name || <span className="text-ui-muted">Pelanggan umum</span>}</td>
                  <td className="p-3 hidden md:table-cell text-ui-muted">{new Date(t.created_at).toLocaleString("id-ID", { timeZone: TRANSACTION_TIME_ZONE, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="p-3 hidden sm:table-cell text-ui-muted">{t.cashier}</td>
                  <td className="p-3 text-center"><span className={`text-xs px-2 py-1 rounded-full capitalize ${badge[t.payment_type]}`}>{t.payment_type}</span></td>
                  <td className="p-3 text-center hidden md:table-cell">
                    {transactionSyncState(t) === "failed" ? <span className="text-[10px] px-2 py-1 rounded-full bg-danger-soft text-danger-readable">Gagal</span>
                      : transactionSyncState(t) === "pending" ? <span className="text-[10px] px-2 py-1 rounded-full bg-warning-soft text-warning-readable">Tertunda</span>
                      : transactionSyncState(t) === "synced" ? <span className="text-[10px] px-2 py-1 rounded-full bg-brand-soft text-brand-readable">Berhasil</span>
                      : <span className="text-[10px] text-ui-muted">—</span>}
                  </td>
                  <td className="p-3 text-right font-mono font-bold text-brand-readable">
                    {rupiah(t.net_total != null ? t.net_total : t.total)}
                    {t.refund_total > 0 && <div className="text-[10px] text-ui-muted line-through">{rupiah(t.total)}</div>}
                  </td>
                  <td className="p-3 text-ui-muted"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
              {(loading || dateError || loadError || txs.length === 0) && (
                <tr><td colSpan={8} className="p-10 text-center text-ui-muted"><Receipt className="h-8 w-8 mx-auto mb-2" />
                  {loading ? pageSize === 'all' ? `Memuat semua transaksi… ${txs.length} baris` : "Memuat transaksi…" : dateError ? "Pilih rentang tanggal yang valid." : loadError ? "Transaksi belum dapat dimuat." : "Tidak ada transaksi pada periode ini."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div> : <div className="rounded-xl border border-ui-line overflow-x-auto" data-testid="returns-table" aria-busy={loading}>
          <table className="w-full text-sm">
            <thead className="bg-ui-card text-ui-muted text-xs uppercase tracking-wide"><tr>
              <th className="text-left p-3">No. Retur</th><th className="text-left p-3">Asal</th><th className="text-left p-3">Jenis / Keterangan</th><th className="text-left p-3">Produk</th><th className="text-left p-3 hidden md:table-cell">Waktu / Petugas</th><th className="text-right p-3">Nilai</th>
            </tr></thead>
            <tbody>
              {!dateError && returnRows.map((row, index) => {
                const isNoReceipt = row.source === 'no_receipt';
                const exchange = row.resolution === 'exchange';
                const label = row.return_type === 'void' ? 'Void / batal transaksi' : exchange ? 'Tukar barang' : row.resolution === 'debt_reduction' ? 'Potong piutang' : 'Refund uang';
                const exchangeStatus = exchange ? ({ pending: 'Menunggu pembayaran di kasir', completed: 'Tukar selesai', cancelled: 'Tukar dibatalkan' }[row.exchange_status] || 'Tukar barang') : '';
                const items = (row.items || []).map(item => `${item.name} × ${item.qty}`).join(', ');
                const amount = exchange ? Number(row.item_amount || 0) : Number(row.cash_refund_amount ?? row.amount ?? 0);
                return <tr key={`${row.source}-${row.id || row.return_no}-${index}`} data-testid={`return-row-${row.id || index}`} className="border-t border-ui-line align-top">
                  <td className="p-3 font-mono whitespace-nowrap">{row.return_no || '—'}</td>
                  <td className="p-3"><div className="font-medium">{isNoReceipt ? 'Tanpa nota' : 'Dari nota'}</div><div className="text-xs text-ui-muted">{row.invoice_no || '—'}{row.customer_name ? ` · ${row.customer_name}` : ''}</div></td>
                  <td className="p-3"><div className={`inline-flex rounded-full px-2 py-1 text-xs ${exchange ? 'bg-brand-soft text-brand-readable' : 'bg-warning-soft text-warning-readable'}`}>{label}</div>{exchangeStatus && <div className="mt-1 text-xs text-ui-muted">{exchangeStatus}</div>}{row.note && <div className="mt-1 max-w-64 whitespace-pre-wrap text-xs text-ui-muted">{row.note}</div>}</td>
                  <td className="p-3 min-w-48">{items || '—'}</td>
                  <td className="p-3 hidden md:table-cell whitespace-nowrap text-ui-muted">{row.at ? new Date(row.at).toLocaleString('id-ID', { timeZone: TRANSACTION_TIME_ZONE }) : '—'}<div className="text-xs">{row.by || '—'}</div></td>
                  <td className="p-3 text-right font-mono font-semibold whitespace-nowrap">{rupiah(amount)}{exchange && Number(row.cash_refund_amount) > 0 && <div className="text-xs font-normal text-ui-muted">Uang kembali: {rupiah(row.cash_refund_amount)}</div>}{exchange && Number(row.difference) > 0 && <div className="text-xs font-normal text-ui-muted">Selisih: {rupiah(row.difference)}</div>}</td>
                </tr>;
              })}
              {(loading || dateError || loadError || returnRows.length === 0) && <tr><td colSpan={6} className="p-10 text-center text-ui-muted"><Undo2 className="h-8 w-8 mx-auto mb-2" />{loading ? 'Memuat riwayat retur…' : dateError ? 'Pilih rentang tanggal yang valid.' : loadError ? 'Riwayat retur belum dapat dimuat.' : 'Tidak ada retur pada periode ini.'}</td></tr>}
            </tbody>
          </table>
        </div>}
        <p aria-live="polite" data-testid="tx-result-summary" className="text-xs text-ui-muted">
          {loading ? "Memuat…" : dateError || loadError ? "" : viewMode === 'returns' ? `Menampilkan ${returnTotal ? (page - 1) * (pageSize === 'all' ? 100 : pageSize) + 1 : 0}–${Math.min(page * (pageSize === 'all' ? 100 : pageSize), returnTotal)} dari ${returnTotal} retur pada periode ini` : pageSize === 'all' ? `Menampilkan semua ${txs.length} transaksi pada periode ini` : `Menampilkan ${total ? (page - 1) * pageSize + 1 : 0}–${Math.min(page * pageSize, total)} dari ${total} transaksi pada periode ini`}
        </p>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { setSelected(null); if (searchParams.get("transaction_id")) setSearchParams({}); }}>
          <div data-testid="tx-detail-modal" className="bg-ui-card border border-ui-line rounded-xl max-w-md w-full p-5 animate-pop max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-3">
              <div className="font-heading font-bold text-lg">{selected.invoice_no}</div>
              <div className="text-xs text-ui-muted">{new Date(selected.created_at).toLocaleString("id-ID", { timeZone: TRANSACTION_TIME_ZONE })} · {selected.cashier}</div>
            </div>

            {selected.no_receipt && !selected.completed && <div className="mb-3 rounded-lg border border-warning/40 bg-warning-soft p-3 space-y-2">
              <div className="text-xs font-semibold text-warning-readable">Retur tanpa nota · harga mengikuti harga produk saat ini</div>
              <Input value={noReceiptSearch} onChange={(event) => setNoReceiptSearch(event.target.value)} placeholder="Cari produk atau scan barcode…" className="h-9 bg-ui-card border-ui-line" autoFocus />
              {noReceiptMatches.length > 0 && <div className="max-h-36 overflow-auto rounded-md border border-ui-line bg-ui-card divide-y divide-ui-line">{noReceiptMatches.map((product) => <button type="button" key={product.id} onClick={() => addNoReceiptProduct(product)} className="w-full px-2 py-2 text-left text-xs hover:bg-ui-soft flex justify-between gap-2"><span className="truncate">{product.name}</span><span className="font-mono whitespace-nowrap">{rupiah(productPrice(product))}</span></button>)}</div>}
            </div>}

            <div className="space-y-2 border-t border-dashed border-ui-line pt-3">
              {selected.items.map((it, i) => <div key={i} className="rounded-md border border-ui-line/70 p-2 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0"><div className="text-ui-muted truncate">{it.name} <span>x{it.qty}</span></div>
                    {variantSummary(it) && <div className="text-xs text-ui-muted">{variantSummary(it)}</div>}
                    <div className="text-[11px] text-ui-muted">{selected.no_receipt ? `Jumlah retur: ${refundQty[i] || 0}` : `Sisa dapat diretur: ${remaining[i]}`} · {rupiah(it.price)}</div></div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`refund-minus-${i}`} disabled={!(refundQty[i] > 0)} onClick={() => setQ(i, (refundQty[i] || 0) - 1)} className="h-7 w-7 rounded bg-ui-card border border-ui-line flex items-center justify-center disabled:opacity-30"><Minus className="h-3.5 w-3.5" /></button>
                    <span data-testid={`refund-qty-${i}`} className="w-7 text-center font-mono text-sm">{refundQty[i] || 0}</span>
                    <button data-testid={`refund-plus-${i}`} disabled={(refundQty[i] || 0) >= remaining[i]} onClick={() => setQ(i, (refundQty[i] || 0) + 1)} className="h-7 w-7 rounded bg-ui-card border border-ui-line flex items-center justify-center disabled:opacity-30"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                {(refundQty[i] || 0) > 0 && <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                  <select aria-label={`Kondisi ${it.name}`} value={returnMeta[i]?.condition || "saleable"}
                    onChange={(event) => updateReturnMeta(i, { condition: event.target.value, return_to_stock: event.target.value !== "no_stock" })}
                    className="h-9 rounded-md bg-ui-card border border-ui-line px-2 text-xs">
                    <option value="saleable">Layak jual</option><option value="damaged">Rusak</option><option value="expired">Kedaluwarsa</option><option value="no_stock">Tidak kembali</option>
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-ui-muted whitespace-nowrap"><input type="checkbox"
                    checked={returnMeta[i]?.return_to_stock !== false && (returnMeta[i]?.condition || "saleable") !== "no_stock"}
                    disabled={(returnMeta[i]?.condition || "saleable") === "no_stock"}
                    onChange={(event) => updateReturnMeta(i, { return_to_stock: event.target.checked })} /> Masuk stok</label>
                </div>}
              </div>)}
            </div>

            <div className="border-t border-dashed border-ui-line mt-3 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-ui-muted"><span>Total awal</span><span className="font-mono">{rupiah(selected.total)}</span></div>
              {selected.refund_total > 0 && <div className="flex justify-between text-danger-readable"><span>Sudah direfund</span><span className="font-mono">-{rupiah(selected.refund_total)}</span></div>}
              <div className="flex justify-between font-bold text-brand-readable"><span>Total bersih</span><span className="font-mono">{rupiah(selected.net_total != null ? selected.net_total : selected.total)}</span></div>
            </div>

            {selected.previous_debts?.length > 0 && <div data-testid="transaction-previous-debts" className="border-t border-ui-line mt-3 pt-3 space-y-2 text-sm">
              {selected.previous_debts.map(debt => <div key={debt.invoice_id} className="flex justify-between gap-2"><span>Piutang sebelumnya ({debt.invoice_no.slice(-4)})</span><span>{rupiah(debt.amount)}</span></div>)}
              <p className="text-xs text-ui-muted">{selected.settlement_reversed_at ? "Pelunasan ini sudah dibatalkan dan saldo piutang telah dipulihkan." : "Piutang sebelumnya sudah dilunasi pada pembayaran ini."}</p>
            </div>}
            {selected.note && <p data-testid="transaction-note" className="mt-3 text-sm whitespace-pre-wrap break-words">Catatan: {selected.note}</p>}

            {canRefundMore && (
              <div className="mt-4 rounded-lg border border-ui-line bg-ui-soft p-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setReturnType("return")} className={`h-9 rounded-md border text-xs font-semibold ${returnType === "return" ? "border-brand bg-brand-soft text-brand-readable" : "border-ui-line bg-ui-card"}`}>Retur Barang</button>
                  {!selected.no_receipt ? <button type="button" onClick={() => { setReturnType("void"); setAll(); }} className={`h-9 rounded-md border text-xs font-semibold ${returnType === "void" ? "border-danger bg-danger-soft text-danger-readable" : "border-ui-line bg-ui-card"}`}>Void/Cancel Semua</button> : <div className="h-9 rounded-md border border-ui-line bg-ui-card text-xs flex items-center justify-center text-ui-muted">Tanpa nota</div>}
                </div>
                <div className="flex items-center justify-between"><span className="text-xs font-semibold text-ui-muted flex items-center gap-1"><Undo2 className="h-4 w-4 text-danger-readable" /> Pilih barang</span>
                  <div className="flex gap-1"><button data-testid="refund-half-btn" onClick={setHalf} className="text-[11px] px-2 h-7 rounded bg-ui-card border border-ui-line hover:border-warning">Separuh</button><button data-testid="refund-all-btn" onClick={setAll} className="text-[11px] px-2 h-7 rounded bg-ui-card border border-ui-line hover:border-danger">Semua</button></div></div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ui-muted">Nilai barang kembali</span>
                  <span data-testid="refund-amount" className="font-mono font-bold text-danger-readable">{rupiah(totalRefundAmount)}</span>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-ui-muted">Penyelesaian</label>
                  <select value={resolution} onChange={(event) => setResolution(event.target.value)} className="h-10 w-full rounded-md bg-ui-card border border-ui-line px-3 text-sm">
                    <option value="refund">Pengembalian uang</option>
                    <option value="exchange">Tukar produk</option>
                    {selected.customer_id && <option value="debt_reduction">Potong utang pelanggan</option>}
                  </select>
                </div>

                {resolution === "exchange" && <div className="rounded-md border border-brand/30 bg-brand-soft p-2.5 text-xs text-brand-readable space-y-1">
                  <div className="font-semibold">Produk pengganti dipilih di layar Kasir</div>
                  <p>Setelah retur disimpan, Kasir terbuka otomatis dengan saldo retur {rupiah(refundAmount)}. Semua produk, satuan (misalnya RCG/Dus), varian, promo, dan beberapa produk sekaligus tetap tersedia.</p>
                </div>}
                {canReverseSettlement && (
                  <label className={`flex items-start gap-2 rounded-md border p-2.5 text-xs ${allRemainingSelected ? "border-warning bg-warning-soft" : "border-ui-line opacity-60"}`}>
                    <input type="checkbox" className="mt-0.5" checked={reverseSettlement} disabled={!allRemainingSelected}
                      onChange={(event) => setReverseSettlement(event.target.checked)} data-testid="reverse-settlement-checkbox" />
                    <span><strong>Batalkan pelunasan piutang {rupiah(selected.settlement_total)}</strong><br />Saldo piutang pelanggan dipulihkan. Hanya tersedia jika semua sisa barang direfund.</span>
                  </label>
                )}
                {resolution === "refund" && <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-ui-muted">Metode Pengembalian Dana</label>
                  <Select value={refundMethod} onValueChange={setRefundMethod}>
                    <SelectTrigger data-testid="refund-method-select" className="bg-ui-card border-ui-line">
                      <SelectValue placeholder="Pilih metode refund" />
                    </SelectTrigger>
                    <SelectContent>
                      {refundMethods.map((method) => (
                        <SelectItem key={method.id} value={method.id}>{method.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-ui-muted">Default Tunai, termasuk untuk transaksi Transfer, QRIS, dan Campuran.</p>
                </div>}
                {resolution === "debt_reduction" && <p className="rounded-md border border-brand/30 bg-brand-soft p-2 text-xs text-brand-readable">Nilai retur mengurangi utang transaksi ini terlebih dahulu. Jika ada kelebihan, sisanya dikembalikan secara Tunai.</p>}
                <div className="space-y-1"><label className="text-xs font-semibold text-ui-muted">Alasan <span className="font-normal">(opsional)</span></label><Input value={returnNote} onChange={(event) => setReturnNote(event.target.value)} maxLength={300} placeholder="Boleh dikosongkan" className="h-9 bg-ui-card border-ui-line" /></div>
                <WriteButton data-testid="confirm-refund-btn" onClick={doRefund} disabled={!anyRefundSelected || refunding} className="w-full h-11 bg-danger hover:bg-danger text-ui-text font-bold gap-2 disabled:opacity-40">
                  <Undo2 className="h-4 w-4" /> {refunding ? "Memproses…" : returnType === "void" ? "Proses Void/Cancel" : "Proses Retur"}
                </WriteButton>
              </div>
            )}

            {selected.refunds?.length > 0 && <div className="mt-4 border-t border-ui-line pt-3 space-y-2"><div className="text-xs font-semibold text-ui-muted">Riwayat Retur / Void</div>{[...selected.refunds].reverse().map((record) => <div key={record.id || record.at} className="rounded-md border border-ui-line p-2 flex items-center justify-between gap-2 text-xs"><div><div className="font-mono font-semibold">{record.return_no || "RETUR LAMA"}</div><div className="text-ui-muted">{new Date(record.at).toLocaleString("id-ID")} · {record.by}</div><div>{rupiah(record.item_amount || record.amount)} · {record.return_type === "void" ? "Void/Cancel" : "Retur"}{record.exchange_status === "pending" ? " · Menunggu tukar" : ""}</div>{record.exchange_status === "completed" && record.cash_refund_amount > 0 && <div className="font-semibold text-danger-readable">Uang kembali ({record.refund_method || "tunai"}): {rupiah(record.cash_refund_amount)}</div>}{record.exchange_status === "completed" && record.additional_payment > 0 && <div className="font-semibold">Selisih dibayar ({record.difference_method || "tunai"}): {rupiah(record.additional_payment)}</div>}</div><div className="flex items-center gap-1">{record.exchange_status === "pending" && <Button type="button" variant="outline" onClick={() => cancelPendingExchange(record)} className="h-8 px-2 border-danger/50 text-danger-readable">Batalkan Tukar</Button>}<Button type="button" variant="outline" onClick={() => printReturnReceipt(selected, record)} className="h-8 px-2 border-ui-line"><Printer className="h-3.5 w-3.5 mr-1" /> Cetak</Button></div></div>)}</div>}

            <div className={`mt-4 grid ${selected.no_receipt ? "grid-cols-1" : "grid-cols-2"} gap-2`}>
              {!selected.no_receipt && <Button data-testid="reprint-btn" variant="outline" onClick={() => doReprint(selected)} className="h-11 border-ui-line bg-transparent hover:bg-ui-card gap-2">
                <Printer className="h-4 w-4" /> Cetak Ulang{selected.refund_total > 0 ? " (Revisi)" : ""}
              </Button>}
              <Button data-testid="close-tx-btn" variant="ghost" onClick={() => { setSelected(null); if (searchParams.get("transaction_id")) setSearchParams({}); }} className="h-11 gap-2 text-ui-muted hover:text-ui-text">
                <X className="h-4 w-4" /> Tutup
              </Button>
            </div>
          </div>
        </div>
      )}

      <ReceiptModal tx={reprint} isReprint cashier={reprint?.cashier} onClose={() => setReprint(null)} />
    </>
  );
}

function TransactionPeriodMenu({ className, testPrefix, selectedPeriod, filters, dateError, periodOpen, setPeriodOpen, updateFilters }) {
  const startId = `${testPrefix}-date-start`;
  const endId = `${testPrefix}-date-end`;
  const errorId = `${testPrefix}-date-error`;
  return (
    <div className={`min-w-0 items-center justify-between gap-2 rounded-lg border border-ui-line bg-ui-card px-2 py-1.5 ${className}`} data-testid={`${testPrefix}-date-filter`}>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-wide text-ui-muted">Periode</div>
        <div className="truncate text-[11px] font-semibold">{selectedPeriod}</div>
      </div>
      <Popover open={periodOpen} onOpenChange={setPeriodOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={`${testPrefix}-period-trigger`} aria-label="Pilih periode tanggal" title="Pilih periode tanggal" className="h-8 w-8 shrink-0 p-0 border-ui-line">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={6} className="w-[min(18rem,calc(100vw-1rem))] bg-ui-card border-ui-line text-ui-text p-2.5 space-y-2">
          <div className="text-xs font-semibold">Periode transaksi</div>
          <div className="grid grid-cols-3 gap-1">
            {PERIODS.map(([key, label]) => {
              const period = transactionPeriod(key);
              const active = filters.start === period.start && filters.end === period.end;
              return <Button key={key} type="button" variant="outline" data-testid={`${testPrefix}-period-${key}`} aria-pressed={active}
                onClick={() => { updateFilters(period); setPeriodOpen(false); }}
                className={`h-8 px-1 text-[10px] border-ui-line ${active ? "bg-brand-soft border-brand/50 text-brand-readable" : "bg-ui-card"}`}>{label}</Button>;
            })}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <label htmlFor={startId} className="min-w-0 text-[10px] text-ui-muted">Dari
              <Input id={startId} data-testid={startId} type="date" required value={filters.start}
                onChange={(event) => updateFilters({ start: event.target.value })}
                aria-invalid={!!dateError} aria-describedby={dateError ? errorId : undefined}
                className="mt-1 h-8 w-full min-w-0 bg-ui-card border-ui-line px-1.5 text-[10px] text-ui-text" />
            </label>
            <label htmlFor={endId} className="min-w-0 text-[10px] text-ui-muted">Sampai
              <Input id={endId} data-testid={endId} type="date" required value={filters.end}
                onChange={(event) => updateFilters({ end: event.target.value })}
                aria-invalid={!!dateError} aria-describedby={dateError ? errorId : undefined}
                className="mt-1 h-8 w-full min-w-0 bg-ui-card border-ui-line px-1.5 text-[10px] text-ui-text" />
            </label>
          </div>
          {dateError && <p id={errorId} role="alert" className="text-[10px] text-danger-readable">{dateError}</p>}
          <Button type="button" onClick={() => setPeriodOpen(false)} className="h-8 w-full bg-brand-button text-xs text-ui-text">Terapkan</Button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
